import React, { useState, useEffect } from 'react';
import { Modal, Input, message } from 'antd';
import { mediaApi } from '../../api/media';
import { plansApi } from '../../api/plans';
import './PlanMedia.css';

function PlanMedia({ plan, orgData, onPlanUpdate }) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedMedia, setSelectedMedia] = useState([]);
	const [availableMedia, setAvailableMedia] = useState([]);
	const [blockId, setBlockId] = useState(null);

	// Helper function to get current media IDs
	const getCurrentMediaIds = () => {
		if (blockId) {
			const block = plan.sections
				.flatMap((s) => s.blocks)
				.find((b) => b.id === blockId);
			return new Set(block?.media?.map((m) => m.id) || []);
		} else {
			return new Set(
				(plan.PlanMedia || plan.media || []).map((m) => m.id)
			);
		}
	};

	// Update useEffect to properly handle media selection
	useEffect(() => {
		if (isModalOpen) {
			const currentMediaIds = getCurrentMediaIds();

			// Find the full media objects that match the current IDs
			const currentMedia = (plan.PlanMedia || plan.media || []).filter(
				(m) => currentMediaIds.has(m.id)
			);

			setSelectedMedia(currentMedia);
		}
	}, [isModalOpen, blockId, plan]);

	const handleOpenModal = (targetBlockId = null) => {
		setBlockId(targetBlockId);
		setIsModalOpen(true);
		setSearchTerm('');
		setAvailableMedia([]);
	};

	const handleSearch = async (value) => {
		try {
			if (!value.trim()) {
				setAvailableMedia([]);
				return;
			}

			const { fileResults } = await mediaApi.files.search(
				orgData.id,
				value
			);
			setAvailableMedia(fileResults);

			// Update selected media to include both existing selections and current search results
			const currentMediaIds = getCurrentMediaIds();
			const currentSelections = selectedMedia.filter((m) =>
				currentMediaIds.has(m.id)
			);
			const newSelectionsFromSearch = fileResults.filter((m) =>
				currentMediaIds.has(m.id)
			);

			// Combine current selections with any new matches from search
			setSelectedMedia([
				...currentSelections,
				...newSelectionsFromSearch,
			]);
		} catch (error) {
			console.error('Error searching media:', error);
			message.error('Failed to search media');
		}
	};

	const handleAddMedia = async () => {
		try {
			const mediaIds = selectedMedia.map((media) => media.id);

			if (blockId) {
				// Adding media to a specific block
				await plansApi.updateBlockMedia(blockId, {
					mediaIds: [
						...(plan.sections
							.flatMap((s) => s.blocks)
							.find((b) => b.id === blockId)
							?.media?.map((m) => m.id) || []),
						...mediaIds,
					],
				});

				// Update the plan with the new block media while preserving other data
				const updatedPlan = {
					...plan,
					sections: plan.sections.map((section) => ({
						...section,
						blocks: section.blocks.map((block) =>
							block.id === blockId
								? {
										...block,
										media: [
											...(block.media || []),
											...selectedMedia,
										],
								  }
								: block
						),
					})),
				};

				onPlanUpdate(updatedPlan);
			} else {
				// Adding media to the plan
				// Include existing media IDs in the API call
				const existingMediaIds =
					plan.PlanMedia?.map((media) => media.id) || [];
				const allMediaIds = [...existingMediaIds, ...mediaIds];

				const response = await plansApi.addPlanMedia(plan.id, mediaIds);

				// Merge the API response with existing plan data
				onPlanUpdate({
					...plan,
					...response.plan,
					sections: plan.sections.map((section) => ({
						...section,
						blocks: section.blocks.map((block) => ({
							...block,
							content: block.content || '',
							notes: block.notes || '',
							media: block.media || [],
						})),
					})),
				});
			}

			setIsModalOpen(false);
			setSelectedMedia([]);
			setSearchTerm('');
			setBlockId(null);
		} catch (error) {
			console.error('Error adding media:', error);
			message.error('Failed to add media');
		}
	};

	const handleRemoveMedia = async (mediaId) => {
		try {
			const response = await plansApi.removePlanMedia(plan.id, mediaId);
			onPlanUpdate(response.plan);
		} catch (error) {
			console.error('Error removing media:', error);
			message.error('Failed to remove media from plan');
		}
	};

	const getMediaIcon = (fileType) => {
		if (fileType?.startsWith('image/')) return 'image';
		if (fileType?.startsWith('audio/')) return 'audiotrack';
		if (fileType?.startsWith('video/')) return 'videocam';
		if (fileType === 'application/pdf') return 'picture_as_pdf';
		return 'insert_drive_file';
	};

	// Get media from either plan.media or plan.PlanMedia
	const planMedia = plan.PlanMedia || plan.media || [];

	return (
		<div className='plan-media-section'>
			<div className='plan-media-header'>
				<h3>Media Files</h3>
				<button
					className='add-media-button-plan-media'
					onClick={() => handleOpenModal()}>
					<span className='material-icons'>add_photo_alternate</span>
				</button>
			</div>
			<div className='media-list'>
				{planMedia.length > 0 ? (
					planMedia.map((media) => (
						<div
							key={media.id}
							className='media-item'>
							<div className='media-item-content'>
								<span className='material-icons'>
									{getMediaIcon(media.fileType)}
								</span>
								<span className='media-name'>{media.name}</span>
							</div>
							<button
								className='remove-media-button'
								onClick={() => handleRemoveMedia(media.id)}>
								<span className='material-icons'>close</span>
							</button>
						</div>
					))
				) : (
					<div className='empty-state'>No media files added yet</div>
				)}
			</div>

			<Modal
				title='Add Media Files'
				open={isModalOpen}
				className='media-modal common-modal'
				onCancel={() => {
					setIsModalOpen(false);
					setSearchTerm('');
					setAvailableMedia([]);
				}}
				onOk={handleAddMedia}>
				<Input
					placeholder='Search media...'
					value={searchTerm}
					onChange={(e) => {
						setSearchTerm(e.target.value);
						handleSearch(e.target.value);
					}}
					prefix={<span className='material-icons'>search</span>}
					className='media-search-input'
				/>
				<div className='media-selection-list'>
					{availableMedia.length > 0 ? (
						availableMedia.map((media) => {
							const isSelected = selectedMedia.some(
								(m) => m.id === media.id
							);
							return (
								<div
									key={media.id}
									className={`media-selection-item ${
										isSelected ? 'selected' : ''
									}`}>
									<input
										type='checkbox'
										id={`media-${media.id}`}
										checked={isSelected}
										onChange={(e) => {
											if (e.target.checked) {
												setSelectedMedia([
													...selectedMedia,
													media,
												]);
											} else {
												setSelectedMedia(
													selectedMedia.filter(
														(m) => m.id !== media.id
													)
												);
											}
										}}
									/>
									<label htmlFor={`media-${media.id}`}>
										<span className='material-icons'>
											{getMediaIcon(media.fileType)}
										</span>
										{media.name}
									</label>
								</div>
							);
						})
					) : (
						<div className='no-results'>No media files found</div>
					)}
				</div>
			</Modal>
		</div>
	);
}

export default PlanMedia;
